import React, { useState } from "react";
import { Compass, CheckCircle2, AlertTriangle, MinusCircle, Sparkles, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const DIRS = [
  { v: "north", label: "North" },
  { v: "north-east", label: "North-East" },
  { v: "east", label: "East" },
  { v: "south-east", label: "South-East" },
  { v: "south", label: "South" },
  { v: "south-west", label: "South-West" },
  { v: "west", label: "West" },
  { v: "north-west", label: "North-West" },
];

const ROOMS = [
  { key: "plot_facing", label: "Main Entrance / Plot Facing", required: true, desc: "Direction your front door opens to" },
  { key: "kitchen", label: "Kitchen", desc: "Cooking platform location" },
  { key: "master_bedroom", label: "Master Bedroom", desc: "Largest bedroom — head of family" },
  { key: "pooja_room", label: "Pooja / Meditation Room", desc: "Spiritual zone" },
  { key: "toilet", label: "Toilet / Bathroom", desc: "Primary bathroom location" },
  { key: "living_room", label: "Living Room", desc: "Main hall / drawing room" },
  { key: "water_tank", label: "Underground Water Tank", desc: "Borewell / sump position" },
];

const STATUS_STYLES = {
  ideal: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2, label: "Ideal" },
  neutral: { bg: "bg-amber-50", border: "border-amber-200", text: "text-[#B88C2E]", icon: MinusCircle, label: "Neutral" },
  avoid: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: AlertTriangle, label: "Avoid" },
  unknown: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", icon: MinusCircle, label: "—" },
};

const VERDICT_STYLES = {
  excellent: { gradient: "from-emerald-500 to-teal-600", text: "text-emerald-50" },
  good: { gradient: "from-amber-400 to-amber-600", text: "text-amber-50" },
  moderate: { gradient: "from-orange-400 to-orange-600", text: "text-orange-50" },
  weak: { gradient: "from-rose-500 to-rose-700", text: "text-rose-50" },
};

export default function VastuCalculator() {
  const [form, setForm] = useState({
    plot_facing: "east", kitchen: "", master_bedroom: "", pooja_room: "", toilet: "", living_room: "", water_tank: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
    try {
      const { data } = await api.post("/vastu", payload);
      setResult(data);
      setTimeout(() => document.getElementById("vastu-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ plot_facing: "east", kitchen: "", master_bedroom: "", pooja_room: "", toilet: "", living_room: "", water_tank: "" });
    setResult(null);
  };

  const verdictStyle = result ? VERDICT_STYLES[result.verdict_class] || VERDICT_STYLES.moderate : null;

  return (
    <div data-testid="vastu-page" className="min-h-screen pb-32">
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-b border-amber-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
          <div className="overline mb-3 text-[#B88C2E] flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Vastu Shastra · Free Tool</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight">
            Vastu <span className="text-[#D4A437]">Compatibility</span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-3xl text-base sm:text-lg font-light">
            Score your home against classical Vastu Shastra rules. Get a 0–100 alignment score with room-wise feedback and remedies.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-7xl mx-auto px-6 md:px-12 mt-12 grid lg:grid-cols-12 gap-10">
        {/* Inputs */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <div className="overline">Pick directions for each room</div>
            <button type="button" onClick={reset} data-testid="vastu-reset"
              className="text-xs text-slate-500 hover:text-[#D4A437] flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          {ROOMS.map((room) => (
            <div key={room.key} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {room.label} {room.required && <span className="text-rose-500">*</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{room.desc}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {DIRS.map((d) => {
                  const active = form[room.key] === d.v;
                  return (
                    <button
                      key={d.v}
                      type="button"
                      onClick={() => update(room.key, active && !room.required ? "" : d.v)}
                      data-testid={`vastu-${room.key}-${d.v}`}
                      className={`py-2 rounded-lg text-xs font-semibold border transition-colors ${
                        active ? "bg-[#D4A437] border-[#D4A437] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-[#D4A437]"
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            type="submit"
            disabled={loading || !form.plot_facing}
            data-testid="vastu-submit"
            className="w-full sm:w-auto px-8 py-3.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            {loading ? "Analysing…" : "Calculate Vastu Score →"}
          </button>
        </div>

        {/* Compass / sidebar */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="bg-slate-900 text-white rounded-2xl p-8">
            <div className="overline text-[#D4A437] mb-4">The 8-Direction Compass</div>
            <div className="relative aspect-square max-w-xs mx-auto">
              <div className="absolute inset-0 rounded-full border-2 border-[#D4A437]/30"></div>
              <div className="absolute inset-4 rounded-full border border-[#D4A437]/20"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Compass className="w-12 h-12 text-[#D4A437]" strokeWidth={1.2} />
              </div>
              {[
                { d: "N", pos: "top-2 left-1/2 -translate-x-1/2" },
                { d: "NE", pos: "top-6 right-6" },
                { d: "E", pos: "top-1/2 right-2 -translate-y-1/2" },
                { d: "SE", pos: "bottom-6 right-6" },
                { d: "S", pos: "bottom-2 left-1/2 -translate-x-1/2" },
                { d: "SW", pos: "bottom-6 left-6" },
                { d: "W", pos: "top-1/2 left-2 -translate-y-1/2" },
                { d: "NW", pos: "top-6 left-6" },
              ].map((p) => (
                <span key={p.d} className={`absolute ${p.pos} text-xs font-semibold tracking-widest text-slate-300`}>{p.d}</span>
              ))}
            </div>
            <div className="text-xs text-slate-400 mt-5 leading-relaxed">
              Use a magnetic compass app to find true North from inside your living room. The direction your <strong className="text-white">main door faces while standing inside</strong> is your "facing".
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex gap-3">
              <Sparkles className="w-5 h-5 text-[#B88C2E] mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700 leading-relaxed">
                Already own a home with Vastu issues? Most can be corrected without breaking walls — using mirrors, pyramids, plants, and color therapy. Talk to our Vastu consultant for a personalised remedy plan.
              </div>
            </div>
          </div>
        </aside>
      </form>

      {/* Result */}
      {result && verdictStyle && (
        <div id="vastu-result" className="max-w-7xl mx-auto px-6 md:px-12 mt-16" data-testid="vastu-result">
          <div className={`rounded-2xl p-8 sm:p-12 bg-gradient-to-br ${verdictStyle.gradient} text-white`}>
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="overline text-white/80 mb-3">Your Vastu Score</div>
                <div className="font-serif text-7xl sm:text-8xl font-light leading-none" data-testid="vastu-score">
                  {result.overall_score}<span className="text-3xl text-white/70">/100</span>
                </div>
                <div className={`text-lg sm:text-xl mt-4 font-light ${verdictStyle.text}`} data-testid="vastu-verdict">
                  {result.verdict}
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-white/15 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <div className="overline text-white/80 mb-3">General Remedies</div>
                  <ul className="space-y-2 text-sm font-light">
                    {result.general_remedies.slice(0, 4).map((r, i) => (
                      <li key={i} className="flex gap-2"><span className="text-white/60">•</span> {r}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Room-wise */}
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            {result.rooms.map((r) => {
              const style = STATUS_STYLES[r.status] || STATUS_STYLES.unknown;
              const Icon = style.icon;
              return (
                <div key={r.room} className={`rounded-xl p-5 border ${style.bg} ${style.border}`} data-testid={`vastu-room-${r.room}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 capitalize">{r.room.replace(/_/g, " ")}</div>
                      <div className="text-xs text-slate-600 mt-0.5 capitalize">Direction: <span className="font-semibold">{r.direction.replace("-", " ")}</span></div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${style.text} bg-white border ${style.border}`}>
                      <Icon className="w-3.5 h-3.5" /> {style.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 mt-3 leading-relaxed">{r.tip}</p>
                  <div className="text-xs text-slate-500 mt-3">
                    Ideal: <span className="capitalize">{r.ideal_directions.map(d => d.replace("-", " ")).join(", ")}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
